import { z } from "zod";
import { somarDias } from "./dia.js";
/**
 * SM-2 — repetição espaçada.
 *
 * **Auditável é o requisito, não um adjetivo.** Três coisas garantem isso, e as
 * três são estruturais:
 *
 *  1. **A função é pura.** Sem banco, sem `Date.now()`, sem aleatoriedade. O
 *     estado anterior entra, o próximo sai. Duas execuções com a mesma entrada
 *     dão o mesmo resultado, sempre — que é o que permite conferir o número na
 *     mão quando ele parecer errado.
 *  2. **Os parâmetros vêm de fora**, da configuração do servidor (invariante 4),
 *     e viajam até o navegador. Nenhum deles é literal aqui.
 *  3. **O cálculo devolve a memória de si mesmo** em `auditoria`: o que entrou,
 *     o que saiu e por quê. A tela imprime isso ao lado do resultado.
 *
 * A queixa contra o sistema anterior era lógica opaca no servidor. A resposta
 * não é "confie": é o número, a conta e a fórmula, no mesmo lugar.
 *
 * A fórmula é a de Piotr Woźniak (1987), com **um** desvio documentado — o
 * tratamento do "com esforço", explicado em `multiplicadorDificil` e em
 * `docs/srs.md` §8:
 *
 *     q < 3  →  repeticoes = 0, intervalo = I1
 *     q ≥ 3  →  repeticoes += 1
 *               repeticoes = 1  →  intervalo = I1
 *               repeticoes = 2  →  intervalo = I2
 *               repeticoes ≥ 3  →  intervalo = round(intervalo_anterior × EF)
 *
 *     EF' = EF + (0,1 − (5 − q) × (0,08 + (5 − q) × 0,02)),  com piso EFmin
 *
 * **A ordem importa e é fácil de errar:** o intervalo multiplica pelo fator
 * **anterior**, e o fator é atualizado depois. Multiplicar pelo fator novo dá
 * 17 dias onde o SM-2 dá 16 — uma diferença pequena que se acumula ao longo de
 * uma carta, e que tornaria o número da tela impossível de conferir contra a
 * fórmula publicada. Foi assim que o teste da carta ao longo de dez revisões
 * pegou a primeira versão desta função.
 *
 * Ver `docs/srs.md` para a tabela por grau e os exemplos resolvidos.
 */
/** Qualidade da lembrança, na escala 0–5 do SM-2. */
export const zQualidade = z.number().int().min(0).max(5);
export const zParametrosSrs = z.object({
    /** Fator de facilidade de um item que nunca foi revisado. */
    fatorInicial: z.number(),
    /** Piso do fator. Abaixo dele os intervalos deixariam de crescer. */
    fatorMinimo: z.number(),
    /** Intervalo depois do primeiro acerto, em dias. */
    intervalo1: z.number().int().positive(),
    /** Intervalo depois do segundo acerto, em dias. */
    intervalo2: z.number().int().positive(),
    /**
     * Multiplicador do "com esforço" (q = 3), no lugar do fator de facilidade.
     *
     * **Este é o único desvio do SM-2 de 1987, e existe por um defeito medido.**
     * No algoritmo original, q = 3 é acerto: as repetições avançam e o intervalo
     * multiplica pelo fator, que nunca desce abaixo do piso de 1,3. O resultado é
     * que um cartão que você acha difícil **toda vez** chega a 327 dias na nona
     * revisão — some da fila justamente por ser difícil.
     *
     * Foi encontrado ao conferir os exemplos de `docs/srs.md` contra o código.
     * Com 1,2, o mesmo cartão anda devagar e continua aparecendo.
     */
    multiplicadorDificil: z.number().positive(),
});
export const zAuditoria = z.object({
    qualidade: z.number().int(),
    origem: z.enum(["autoavaliacao", "derivada"]),
    fatorAntes: z.number(),
    fatorDepois: z.number(),
    ajusteDoFator: z.number(),
    pisoAplicado: z.boolean(),
    repeticoesAntes: z.number().int(),
    repeticoesDepois: z.number().int(),
    intervaloAntes: z.number().int(),
    intervaloDepois: z.number().int(),
    multiplicadorAplicado: z.number().nullable(),
    regra: z.enum(["lapso", "primeira", "segunda", "multiplicacao", "dificil"]),
    lapso: z.boolean(),
});
/** Estado de um item que nunca foi revisado. */
export function estadoInicial(p) {
    return { fatorFacilidade: p.fatorInicial, intervaloDias: 0, repeticoes: 0, lapsos: 0 };
}
/**
 * O ajuste do fator de facilidade para uma qualidade.
 *
 * Separado para poder ser exercitado grau a grau, e para a documentação e a
 * tela lerem a mesma conta que o agendamento usa.
 */
export function ajusteDoFator(qualidade) {
    const distancia = 5 - qualidade;
    return 0.1 - distancia * (0.08 + distancia * 0.02);
}
/** Duas casas: o fator é comparado e exibido, e ponto flutuante cru não fecha. */
function arredondarFator(valor) {
    return Math.round(valor * 100) / 100;
}
/**
 * Aplica uma revisão e devolve o próximo estado.
 *
 * `hoje` é o dia **do usuário** (invariante 16), não o dia UTC: quem chama já
 * converteu com `hojeDoUsuario`. A próxima revisão sai de `somarDias`, que
 * trabalha em dia civil e não em milissegundos — 24 horas não é um dia em
 * semana de horário de verão.
 */
export function revisar(estado, qualidade, hoje, parametros, origem = "autoavaliacao") {
    const anterior = estado ?? estadoInicial(parametros);
    const lapso = qualidade < 3;
    const ajuste = ajusteDoFator(qualidade);
    const bruto = arredondarFator(anterior.fatorFacilidade + ajuste);
    const fatorDepois = Math.max(bruto, parametros.fatorMinimo);
    // O intervalo usa o fator **anterior**, não o recém-calculado. Ver a nota
    // sobre a ordem no cabeçalho.
    let repeticoes;
    let intervaloDias;
    let regra;
    let multiplicador = null;
    if (lapso) {
        // O lapso reinicia a contagem, mas **não** o fator: o histórico de
        // dificuldade do item é justamente o que não deve ser esquecido.
        repeticoes = 0;
        intervaloDias = parametros.intervalo1;
        regra = "lapso";
    }
    else {
        repeticoes = anterior.repeticoes + 1;
        if (repeticoes === 1) {
            intervaloDias = parametros.intervalo1;
            regra = "primeira";
        }
        else if (repeticoes === 2) {
            intervaloDias = parametros.intervalo2;
            regra = "segunda";
        }
        else if (qualidade === 3) {
            // O desvio documentado: "com esforço" cresce devagar, em vez de
            // multiplicar pelo fator. Ver a nota em `multiplicadorDificil`.
            multiplicador = parametros.multiplicadorDificil;
            intervaloDias = Math.round(anterior.intervaloDias * multiplicador);
            regra = "dificil";
        }
        else {
            multiplicador = anterior.fatorFacilidade;
            intervaloDias = Math.round(anterior.intervaloDias * multiplicador);
            regra = "multiplicacao";
        }
        // Um item com intervalo anterior curto poderia não crescer nunca, com o
        // fator no piso ou com o multiplicador do "com esforço". O intervalo tem
        // que andar pelo menos um dia.
        if (regra !== "primeira" && regra !== "segunda" && intervaloDias <= anterior.intervaloDias) {
            intervaloDias = anterior.intervaloDias + 1;
        }
    }
    return {
        fatorFacilidade: fatorDepois,
        intervaloDias,
        repeticoes,
        lapsos: anterior.lapsos + (lapso ? 1 : 0),
        proximaRevisao: somarDias(hoje, intervaloDias),
        auditoria: {
            qualidade,
            origem,
            fatorAntes: anterior.fatorFacilidade,
            fatorDepois,
            ajusteDoFator: arredondarFator(ajuste),
            pisoAplicado: bruto < parametros.fatorMinimo,
            repeticoesAntes: anterior.repeticoes,
            repeticoesDepois: repeticoes,
            intervaloAntes: anterior.intervaloDias,
            intervaloDepois: intervaloDias,
            multiplicadorAplicado: multiplicador,
            regra,
            lapso,
        },
    };
}
/**
 * A qualidade de um item objetivo, derivada do acerto.
 *
 * Múltipla escolha, certo/errado e cloze não pedem nota nenhuma: o servidor já
 * sabe se acertou. Pedir uma autoavaliação depois de o app ter dito "errou"
 * seria atrito sem informação.
 *
 * O erro vale **2**, não 0. Os dois reiniciam o intervalo, mas o 2 castiga
 * menos o fator (−0,32 contra −0,80): errar uma múltipla de cinco alternativas
 * pode ser chute infeliz, enquanto branco total num cartão é outra coisa.
 *
 * O acerto vale **4**, que é o grau neutro — não mexe no fator. É o palpite
 * honesto: a máquina não tem como saber se foi fácil ou custou. `ajuste`
 * permite ao usuário corrigir com um toque, sem obrigá-lo a nada.
 */
export const QUALIDADE_ERRO_OBJETIVO = 2;
export const QUALIDADE_ACERTO_OBJETIVO = 4;
export function qualidadeDerivada(acertou, ajuste) {
    if (!acertou)
        return QUALIDADE_ERRO_OBJETIVO;
    if (ajuste === "dificil")
        return 3;
    if (ajuste === "facil")
        return 5;
    return QUALIDADE_ACERTO_OBJETIVO;
}
/** Uma frase curta para a tela: "volta em 6 dias". RN-13. */
export function frasearIntervalo(dias) {
    if (dias <= 0)
        return "volta hoje";
    if (dias === 1)
        return "volta amanhã";
    if (dias < 30)
        return `volta em ${dias} dias`;
    const meses = Math.round(dias / 30);
    return meses === 1 ? "volta em cerca de um mês" : `volta em cerca de ${meses} meses`;
}
/* ------------------------------------------------ balanceamento da carga */
/**
 * Os dois números que decidem o quanto o agendamento pode andar para achar um
 * dia mais vazio. Configuração, não constante de código (invariante 4).
 */
export const zBalanceamento = z.object({
    /**
     * A partir de que intervalo o dia pode ser deslocado.
     *
     * Abaixo dele não há o que negociar: o intervalo de 1 e o de 6 são os dois
     * degraus fixos do SM-2, e mover o de 1 dia significaria revisar no mesmo dia
     * ou pular um — o que muda o algoritmo, e não a distribuição dele.
     */
    intervaloMinimo: z.number().int().positive(),
    /**
     * Quanto do intervalo o dia pode andar para cada lado, em fração.
     *
     * 5% de 30 dias é dia e meio: o esquecimento não muda em nada, e a fila do
     * dia deixa de ter um pico. O piso de um dia é o que faz a regra valer também
     * para os intervalos curtos, onde 5% arredondaria para zero.
     */
    folgaFracao: z.number().positive(),
});
export const BALANCEAMENTO_PADRAO = { intervaloMinimo: 4, folgaFracao: 0.05 };
/**
 * O dia em que a revisão vai cair de verdade — o proposto pelo SM-2, ou um
 * vizinho mais vazio.
 *
 * **O algoritmo de espaçamento não muda em nada.** O intervalo continua sendo o
 * que o SM-2 calculou, e a auditoria continua registrando esse número. O que
 * esta função faz é escolher, dentro de uma folga proporcional ao próprio
 * intervalo, o dia com menos revisões marcadas — e é o que impede que um dia de
 * estudo intenso vire um muro no dia seguinte, e outro muro seis dias depois.
 *
 * **Empate vai para o dia proposto**, e depois para o mais próximo dele. Sem
 * isso, o balanceamento ficaria puxando tudo para o começo da janela e
 * inventaria um pico novo na borda.
 *
 * `carga` é a contagem de revisões já marcadas por dia. Quem a monta é quem tem
 * o banco; esta função é pura para poder ser exercitada sem ele.
 */
export function diaBalanceado(proposto, intervaloDias, carga, parametros = BALANCEAMENTO_PADRAO) {
    if (intervaloDias < parametros.intervaloMinimo)
        return proposto;
    const folga = Math.max(1, Math.round(intervaloDias * parametros.folgaFracao));
    let escolhido = proposto;
    let menor = carga.get(proposto) ?? 0;
    let distancia = 0;
    for (let d = -folga; d <= folga; d++) {
        if (d === 0)
            continue;
        const dia = somarDias(proposto, d);
        const n = carga.get(dia) ?? 0;
        const dist = Math.abs(d);
        if (n < menor || (n === menor && dist < distancia)) {
            escolhido = dia;
            menor = n;
            distancia = dist;
        }
    }
    return escolhido;
}
//# sourceMappingURL=srs.js.map